import { type FC } from "react";

import {
  DocumentKindEnum,
  type TranscriptDocumentFragment,
  TranscriptStateEnum,
} from "@app_schema";

import { formatClock } from "@application/utilities/format_clock";

import { FileQueue } from "@styled/file_queue";
import { FileRail, FileRailItem } from "@styled/file_rail";

import {
  UploadButton,
  filePillStatus,
  renderFileQueueItem,
} from "./shared_card_utils";

// What a dictation file is, by the kind the server stored it as.
export const transcriptKindLabel = (kind?: DocumentKindEnum | null): string => {
  if (kind === DocumentKindEnum.AudioRecording) return "Dictation";
  if (kind === DocumentKindEnum.DictationNotes) return "Notes";
  return "File";
};

// The second line under a file: what it is, then its length once
// transcribed, or where it stands until then.
const fact = (document: TranscriptDocumentFragment): string | undefined => {
  if (document.transcriptState === TranscriptStateEnum.Failed) return "Failed";
  if (document.transcriptState !== TranscriptStateEnum.Ready)
    return filePillStatus(document)?.text;
  if (document.kind === DocumentKindEnum.DictationNotes) return "Word";
  const seconds = document.transcript?.durationSeconds;
  return seconds ? formatClock(seconds) : undefined;
};

const detail = (document: TranscriptDocumentFragment): string =>
  [transcriptKindLabel(document.kind), fact(document)]
    .filter(Boolean)
    .join(" · ");

// Every dictation file in the case, one selected, and the upload below.
export const TranscriptFiles: FC<{
  documents: TranscriptDocumentFragment[];
  selectedId: string;
  accept: string;
  onSelect(id: string): void;
}> = ({ documents, selectedId, accept, onSelect }) => (
  <FileRail
    label="Files"
    footer={
      <UploadButton
        variant="add"
        accept={accept}
        label="Add audio or notes"
        color="white"
      />
    }
  >
    {documents.map((document) => (
      <FileRailItem
        key={document.id}
        name={document.name}
        detail={detail(document)}
        active={document.id === selectedId}
        onClick={() => onSelect(document.id)}
      />
    ))}
    <FileQueue render={renderFileQueueItem} />
  </FileRail>
);
